<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { ref, watch } from 'vue';

import AutoDeliveryIntervalSelector from '@/components/auto-delivery/AutoDeliveryIntervalSelector.vue';
import FormControlsWrapper from '@/components/base/form/FormControlsWrapper.vue';
import Modal from '@/components/base/layout/Modal.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import ToastNotification from '@/components/base/ToastNotification.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { NutsLineItem } from '@/lib/cart/lineItem';

const props = defineProps<{
  isLoading?: boolean;
  isOpen: boolean;
  lineItems: NutsLineItem[];
  modelValue: number;
}>();
const emit = defineEmits<{ 'handle-close': [] }>();

const interval = useVModel(props, 'modelValue', emit);
const notificationMessage = ref<string>();

watch(interval, () => {
  notificationMessage.value = `Auto-Delivery Frequency has been updated to ${interval.value} weeks`;
});
</script>

<template>
  <Modal
    anchor="right"
    aria-label="Adjust frequency"
    data-test="auto-delivery-adjust-frequency-modal"
    :hasControls="false"
    :isOpen="isOpen"
    skipFirstElementOnFocus
    width="md:max-w-lg"
    @handle-close="$emit('handle-close')"
  >
    <template #header>
      <Header5 headerTag="span">Adjust Frequency</Header5>
    </template>
    <template #body>
      <div class="flex flex-col gap-3 mt-6 md:gap-4 md:mt-8">
        <FormControlsWrapper class="flex flex-col gap-4 cursor-auto" selected>
          <div>
            <div class="flex items-center justify-center w-full">
              <ToastNotification
                :centeredOnViewport="false"
                :show="!!notificationMessage"
                @hide="notificationMessage = ''"
              >
                <SmallBodyText>{{ notificationMessage }}</SmallBodyText>
              </ToastNotification>
            </div>
            <BaseBodyText class="font-semibold">Auto-Delivery Shipment</BaseBodyText>
            <SmallBodyText class="text-neutral-600">
              Won't activate until you checkout
            </SmallBodyText>
          </div>
          <ul class="grid grid-cols-4 gap-3">
            <li
              v-for="lineItem in lineItems.slice(0, 3)"
              class="bg-white rounded-md"
              :key="lineItem.id"
            >
              <img
                :alt="lineItem.name.en"
                class="object-contain w-full h-16"
                :src="lineItem.titleImage?.medium"
              />
            </li>
            <li
              v-if="lineItems.length > 3"
              class="flex flex-col items-center justify-center w-full h-16 rounded-md bg-nuts-neutral-200"
            >
              <Header4 headerTag="p">+{{ lineItems.length - 3 }}</Header4>
              <SmallBodyText>more</SmallBodyText>
            </li>
          </ul>
          <AutoDeliveryIntervalSelector v-model="interval" :isLoading="isLoading" />
        </FormControlsWrapper>
        <SmallBodyText class="text-neutral-600">
          *All products selected for Auto-Delivery in the cart must have the same subscription
          frequency.
          <PopperWrapper arrow class="legacy-max-width" hover theme="dark">
            <SmallBodyText class="font-semibold underline cursor-pointer">Learn more</SmallBodyText>
            <template #content>
              <div class="max-w-xs p-4">
                <LargeBodyText class="font-normal text-white">
                  Frequency restriction
                </LargeBodyText>
                <SmallBodyText class="mt-2 font-normal text-white">
                  In order for all products in your Auto-Delivery order to ship together, they must
                  be set to the same shipping frequency
                </SmallBodyText>
              </div>
            </template>
          </PopperWrapper>
        </SmallBodyText>
      </div>
    </template>
  </Modal>
</template>
